import { api } from './client';

function envelope(res: any): any {
  return res && typeof res === 'object' && 'data' in res ? res.data : res;
}

function asList(res: any): any[] {
  const data = envelope(res);
  return Array.isArray(data) ? data : [];
}

function asPage(res: any) {
  const data = asList(res);
  const pagination = res?.pagination ?? {};
  return {
    data,
    total: res?.total ?? pagination.total ?? data.length,
    page: res?.page ?? pagination.page ?? 1,
    pageSize: res?.pageSize ?? pagination.pageSize ?? data.length,
    totalPages: res?.totalPages ?? pagination.totalPages ?? 1,
  };
}

function normalizeDevice(row: any) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    hostname: row.hostname ?? '',
    client: row.client ?? row.client_name ?? 'Unassigned',
    site: row.site ?? row.site_name ?? 'Unassigned',
    clientId: row.clientId ?? row.client_id ?? '',
    siteId: row.siteId ?? row.site_id ?? '',
    os: row.os ?? row.os_name ?? '',
    osVersion: row.osVersion ?? row.os_version ?? '',
    agentVersion: row.agentVersion ?? row.agent_version ?? '',
    loggedInUser: row.loggedInUser ?? row.logged_in_user ?? null,
    publicIp: row.publicIp ?? row.public_ip ?? '',
    localIp: row.localIp ?? row.local_ip ?? '',
    status: row.status || 'offline',
    cpu: Number(row.cpu ?? row.cpu_usage ?? 0) || 0,
    memory: Number(row.memory ?? row.memory_usage ?? 0) || 0,
    disk: Number(row.disk ?? row.disk_usage ?? 0) || 0,
    patchStatus: row.patchStatus ?? row.patch_status ?? 'compliant',
    antivirus: row.antivirus ?? row.antivirus_status ?? 'not_installed',
    pendingReboot: !!(row.pendingReboot ?? row.pending_reboot),
    lastSeen: row.lastSeen ?? row.last_seen ?? '',
    enrolledAt: row.enrolledAt ?? row.enrolled_at ?? '',
    lastBoot: row.lastBoot ?? row.last_boot ?? '',
    manufacturer: row.manufacturer ?? '',
    model: row.model ?? '',
    serialNumber: row.serialNumber ?? row.serial_number ?? '',
    cpuModel: row.cpuModel ?? row.cpu_model ?? '',
    cpuCores: Number(row.cpuCores ?? row.cpu_cores ?? 0) || 0,
    memoryTotal: Number(row.memoryTotal ?? row.memory_total ?? row.memory_total_gb ?? 0) || 0,
    memoryUsed: Number(row.memoryUsed ?? row.memory_used ?? 0) || 0,
    uptime: Number(row.uptime ?? row.uptime_seconds ?? 0) || 0,
    tags: Array.isArray(row.tags) ? row.tags : [],
    patches: Array.isArray(row.patches) ? row.patches : [],
    installedSoftware: Array.isArray(row.installedSoftware)
      ? row.installedSoftware
      : (Array.isArray(row.installed_software) ? row.installed_software : []),
    disks: Array.isArray(row.disks) ? row.disks : [],
    networkInterfaces: Array.isArray(row.networkInterfaces) ? row.networkInterfaces : [],
    services: Array.isArray(row.services) ? row.services : [],
    processes: Array.isArray(row.processes) ? row.processes : [],
    eventLogs: Array.isArray(row.eventLogs) ? row.eventLogs : [],
    recentAlerts: Array.isArray(row.recentAlerts) ? row.recentAlerts : [],
    recentJobs: Array.isArray(row.recentJobs) ? row.recentJobs : [],
    recentSessions: Array.isArray(row.recentSessions) ? row.recentSessions : [],
  };
}

function normalizeUser(row: any) {
  if (!row || typeof row !== 'object') return row;
  const roles = Array.isArray(row.roles) ? row.roles : [];
  const roleName = row.role ?? roles[0]?.name ?? 'User';
  const teams = Array.isArray(row.teams)
    ? row.teams.map((t: any) => (typeof t === 'string' ? t : t?.name)).filter(Boolean)
    : [];
  return {
    ...row,
    fullName: row.fullName ?? row.full_name ?? row.email ?? '',
    email: row.email ?? '',
    role: typeof roleName === 'string' ? roleName : 'User',
    roles,
    teams,
    status: row.status ?? 'active',
    mfaEnabled: !!(row.mfaEnabled ?? row.mfa_enabled),
    lastLogin: row.lastLogin ?? row.last_login ?? null,
    avatar: row.avatar ?? '',
    createdAt: row.createdAt ?? row.created_date ?? '',
  };
}

function normalizeRole(row: any) {
  if (!row || typeof row !== 'object') return row;
  const permissions = Array.isArray(row.permissions)
    ? row.permissions.map((p: any) => (typeof p === 'string' ? p : p?.name)).filter(Boolean)
    : [];
  return {
    ...row,
    name: row.name ?? '',
    description: row.description ?? '',
    color: row.color ?? '#71717a',
    isSystem: !!(row.isSystem ?? row.is_system),
    permissions,
    userCount: Number(row.userCount ?? row.user_count ?? 0) || 0,
  };
}

function normalizeClient(row: any) {
  if (!row || typeof row !== 'object') return row;
  return {
    id: row.id,
    tenantId: row.tenantId ?? row.tenant_id ?? '',
    name: row.name ?? '',
    contactName: row.contactName ?? row.contact_name ?? '',
    contactEmail: row.contactEmail ?? row.contact_email ?? '',
    contactPhone: row.contactPhone ?? row.contact_phone ?? '',
    siteCount: Number(row.siteCount ?? row.sites_count ?? row.site_count ?? 0) || 0,
    deviceCount: Number(row.deviceCount ?? row.devices_count ?? row.device_count ?? 0) || 0,
    status: row.status || 'active',
    createdAt: row.createdAt ?? row.created_date ?? '',
  };
}

function emptyOverview() {
  return {
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    warningDevices: 0,
    criticalDevices: 0,
    needingAttention: 0,
    activeAlerts: 0,
    criticalAlerts: 0,
    patchCompliance: 100,
    antivirusActive: 0,
    pendingReboots: 0,
    runningJobs: 0,
    recentlyEnrolled: 0,
    activeRemoteSessions: 0,
    avgCpu: 0,
    avgMemory: 0,
    avgDisk: 0,
    alertTrend: [] as Array<{ date: string; critical: number; warning: number; resolved: number }>,
    deviceStatusDistribution: [] as Array<{ label: string; value: number; color: string }>,
  };
}

export const realApi = {
  getDevices: async (params?: Record<string, any>) => {
    const clean: Record<string, any> = {};
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '' || value === 'all') continue;
        if (Array.isArray(value) && value.length === 0) continue;
        clean[key] = value;
      }
    }
    const page = asPage(await api.get('/devices', Object.keys(clean).length ? clean : undefined));
    return { ...page, data: page.data.map(normalizeDevice) };
  },
  getDevice: async (id: string) => normalizeDevice(envelope(await api.get(`/devices/${id}`))),
  deviceAction: async (id: string, action: string, extra: Record<string, any> = {}) =>
    envelope(await api.post(`/devices/${id}/action`, { action, ...extra })),
  runDeviceCommand: async (id: string, payload: { shell?: string; script?: string; command?: string }) =>
    envelope(await api.post(`/devices/${id}/run`, payload)),
  getDeviceCommand: async (id: string, commandId: string) =>
    envelope(await api.get(`/devices/${id}/commands/${commandId}`)),
  zipRemoteFiles: async (id: string, payload: { paths: string[]; zipName: string; destDir: string }) => {
    const script = JSON.stringify({
      paths: payload.paths,
      zipName: payload.zipName,
      destDir: payload.destDir,
    });
    const body = {
      shell: 'zip',
      path: payload.paths[0] || '',
      script,
      zipName: payload.zipName,
      destDir: payload.destDir,
      paths: payload.paths,
    };
    try {
      return envelope(await api.post(`/devices/${id}/files/zip`, payload));
    } catch (error: any) {
      const missing = error?.status === 404 || /endpoint not found/i.test(String(error?.message || ''));
      if (!missing) throw error;
      return envelope(await api.post(`/devices/${id}/run`, body));
    }
  },
  getDrives: async (id: string) => {
    const device = normalizeDevice(envelope(await api.get(`/devices/${id}`)));
    const disks = Array.isArray(device?.disks) ? device.disks : [];
    if (disks.length === 0) {
      return [{ letter: 'C:', label: 'System', totalGb: 0, usedGb: 0, type: 'ssd' as const }];
    }
    return disks.map((disk: any) => ({
      letter: String(disk.drive || disk.letter || 'C:').replace(/\\+$/, ''),
      label: disk.label || 'Local Disk',
      totalGb: Number(disk.totalGb ?? disk.total_gb ?? 0) || 0,
      usedGb: Number(disk.usedGb ?? disk.used_gb ?? 0) || 0,
      type: 'ssd' as const,
    }));
  },
  listFiles: async (id: string, path: string) => {
    const res: any = await api.get(`/devices/${id}/files`, { path });
    const data = envelope(res);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.files)) return data.files;
    if (Array.isArray(res) ) return res;
    return [];
  },
  requestFileDownload: async (id: string, path: string) =>
    envelope(await api.post(`/devices/${id}/files/download`, { path })),
  getFileDownloadStatus: async (id: string, commandId: string) =>
    envelope(await api.get(`/devices/${id}/files/downloads/${commandId}`)),
  getFileDownloadChunk: async (id: string, commandId: string, index: number) => {
    try {
      return await api.getBlob(`/devices/${id}/files/downloads/${commandId}/chunks/${index}`, {
        cache: 'no-store',
      });
    } catch (error: any) {
      if (error?.status === 409) return null;
      throw error;
    }
  },
  cancelFileDownload: async (id: string, commandId: string) => {
    try {
      await api.post(`/devices/${id}/files/downloads/${commandId}/cancel`, {});
    } catch {
      /* ignore */
    }
  },
  streamFileDownload: async (
    id: string,
    commandId: string,
    writable: { write: (data: Uint8Array | Blob) => Promise<unknown>; close: () => Promise<unknown>; abort?: () => Promise<unknown> },
    onProgress?: (info: { written: number; total: number; received: number }) => void
  ) => {
    const prefetch = 8;
    const ready = new Map<number, Blob>();
    const inflight = new Map<number, Promise<Blob | null>>();
    let next = 0;
    let written = 0;
    let total = 0;
    let received = 0;
    let filename = 'download.bin';
    let agentDone = false;
    let complete = false;
    let failed = '';
    let miss = 0;
    const deadline = Date.now() + 12 * 60 * 60 * 1000;
    const chunkPath = (index: number) => `/devices/${id}/files/downloads/${commandId}/chunks/${index}`;

    const throwIfFailed = async (response: Response) => {
      if (response.ok || response.status === 409) return;
      let message = response.statusText || 'Download failed';
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          const body = await response.json();
          message = body?.error?.message || body?.message || message;
        } catch {
          /* keep status text */
        }
      }
      const error = new Error(message) as any;
      error.status = response.status;
      throw error;
    };

    const pullAhead = (index: number) => {
      if (index === next || ready.has(index) || inflight.has(index)) return;
      const req = api.getBlob(chunkPath(index)).then((blob) => {
        inflight.delete(index);
        ready.set(index, blob);
        return blob;
      }).catch((error: any) => {
        inflight.delete(index);
        if (error?.status === 409) return null;
        throw error;
      });
      inflight.set(index, req);
    };

    const refreshStatus = async () => {
      const status: any = await envelope(await api.get(`/devices/${id}/files/downloads/${commandId}`));
      total = Number(status?.total ?? total) || total;
      received = Number(status?.received ?? received) || received;
      filename = status?.filename || filename;
      agentDone = !!status?.agentDone || !!status?.complete;
      complete = !!status?.complete;
      if (status?.status === 'acknowledged' && status?.error && !status?.complete) {
        failed = status.error || 'Agent failed to read the file';
      }
      return Array.isArray(status?.available) ? status.available.map((n: any) => Number(n)) : [];
    };

    const report = () => onProgress?.({ written, total: total || written, received: Math.max(received, written) });

    while (Date.now() < deadline) {
      if (failed) {
        throw new Error(failed);
      }
      for (let i = 1; i <= prefetch; i++) {
        pullAhead(next + i);
      }

      const buffered = ready.get(next);
      if (buffered) {
        ready.delete(next);
        if (buffered.size > 0) {
          await writable.write(buffered);
        }
        written += buffered.size;
        next += 1;
        miss = 0;
        report();
        continue;
      }

      const pending = inflight.get(next);
      if (pending) {
        await pending;
        continue;
      }

      const response = await api.fetchRaw(chunkPath(next));
      await throwIfFailed(response);
      if (response.status === 409) {
        const available = await refreshStatus();
        report();
        if (complete || (agentDone && written >= total && (total > 0 || next > 0))) {
          return { filename, size: written };
        }
        if (agentDone && !available.includes(next) && received > 0 && written < total) {
          miss += 1;
          if (miss > 80) {
            throw new Error(`Download stalled at chunk ${next} (${written} of ${total} bytes)`);
          }
        } else {
          miss = 0;
        }
        await new Promise((resolve) => setTimeout(resolve, 25));
        continue;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        const blob = await response.blob();
        if (blob.size > 0) {
          await writable.write(blob);
        }
        written += blob.size;
      } else {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value && value.byteLength > 0) {
            await writable.write(value);
            written += value.byteLength;
            report();
          }
        }
      }
      next += 1;
      miss = 0;
      report();
    }
    throw new Error('Timed out waiting for the file transfer');
  },
  captureScreenshot: async (id: string) =>
    envelope(await api.post(`/devices/${id}/screenshot`, {})),
  getFileOperations: async (id: string) => asList(await api.get(`/devices/${id}/files`)),
  updateDevice: async (id: string, updates: Record<string, any>) =>
    envelope(await api.patch(`/devices/${id}`, updates)),
  deleteDevice: async (id: string) => {
    await api.post(`/devices/${id}/delete`, {});
  },
  deleteOfflineDevices: async () =>
    envelope(await api.post('/devices/delete-offline', {})) as { deleted?: number },

  getClients: async () => asList(await api.get('/clients')).map(normalizeClient),
  createClient: async (payload: Record<string, any>) =>
    normalizeClient(envelope(await api.post('/clients', {
      name: payload.name,
      contact_name: payload.contactName ?? payload.contact_name ?? null,
      contact_email: payload.contactEmail ?? payload.contact_email ?? null,
      contact_phone: payload.contactPhone ?? payload.contact_phone ?? null,
      status: payload.status ?? 'active',
    }))),
  getSites: async (params?: Record<string, any>) => asList(await api.get('/sites', params)),

  getAlerts: async (params?: Record<string, any>) => asList(await api.get('/alerts', params)),
  acknowledgeAlert: async (id: string) => api.patch(`/alerts/${id}`, { state: 'acknowledged' }),
  resolveAlert: async (id: string) => api.patch(`/alerts/${id}`, { state: 'resolved' }),
  getAlertRules: async () => asList(await api.get('/alert-rules')),
  toggleAlertRule: async (id: string, enabled: boolean) =>
    api.patch(`/alert-rules/${id}`, { enabled }),

  getScripts: async () => asList(await api.get('/scripts')),
  getScript: async (id: string) => envelope(await api.get(`/scripts/${id}`)),
  createScript: async (script: Record<string, any>) => envelope(await api.post('/scripts', script)),
  runScript: async (scriptId: string, deviceIds: string[]) =>
    envelope(await api.post(`/scripts/${scriptId}/run`, { deviceIds })),

  getJobs: async (params?: Record<string, any>) => asList(await api.get('/jobs', params)),
  createJob: async (job: Record<string, any>) => envelope(await api.post('/jobs', job)),

  getUsers: async () => asList(await api.get('/users')).map(normalizeUser),
  getRoles: async () => asList(await api.get('/roles')).map(normalizeRole),

  getAuditLog: async (params?: Record<string, any>) => asList(await api.get('/audit-log', params)),

  getRemoteSessions: async (params?: Record<string, any>) => asList(await api.get('/remote-sessions', params)),

  getPatchPolicies: async () => asList(await api.get('/patch-policies')),

  getActivityFeed: async () => {
    const res: any = await api.get('/reports/activity');
    const payload = envelope(res) ?? {};
    const rows = Array.isArray(payload) ? payload : (payload.recentActivity ?? []);
    return rows.map((row: any) => ({
      id: String(row.id ?? crypto.randomUUID()),
      type: (row.category === 'auth' ? 'enrollment' : row.category) || 'job',
      message: row.action || row.message || 'Activity',
      hostname: row.hostname ?? null,
      userName: row.user_name ?? row.userName ?? null,
      timestamp: row.timestamp || row.created_date || new Date().toISOString(),
      severity: row.result === 'failure' || row.result === 'denied' ? 'warning' : 'info',
    }));
  },
  getOverviewMetrics: async () => {
    const raw = envelope(await api.get('/reports/overview')) ?? {};
    return { ...emptyOverview(), ...raw, alertTrend: raw.alertTrend ?? [], deviceStatusDistribution: raw.deviceStatusDistribution ?? emptyOverview().deviceStatusDistribution };
  },

  saveSettings: async (section: string, data: Record<string, any>) => {
    return api.patch('/settings', { section, data });
  },

  login: async (email: string, password: string, tenant?: string) => {
    const res: any = await api.post('/auth/login', { email, password, tenant });
    const data = res?.accessToken ? res : (res?.data ?? res);
    const access = data?.accessToken || data?.token;
    if (access) {
      api.setTokens(access, data?.refreshToken);
    }
    return { token: access, ...data };
  },
};

// ─── Namespaced exports for future use ────────────────────────────
export const realAuth = {
  login: (email: string, password: string, mfaCode?: string, tenant?: string) =>
    api.post('/auth/login', { email, password, mfaCode, tenant }),
  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
};

export const realDevices = realApi;
export const realAlerts = realApi;
export const realScripts = realApi;
export const realJobs = realApi;
export const realPatches = realApi;
export const realReports = realApi;
export const realRemote = realApi;
export const realTerminal = realApi;
export const realFiles = realApi;
export const realAudit = realApi;
export const realUsers = realApi;
export const realRoles = { list: () => api.get('/roles') };
export const realTeams = { list: () => api.get('/teams') };
export const realClients = { list: () => api.get('/clients') };
export const realSites = { list: () => api.get('/sites') };
export const realSettings = {
  get: () => api.get('/settings'),
  update: (section: string, data: Record<string, any>) =>
    api.patch('/settings', { section, data }),
};
export const realEnrollment = {
  createToken: (clientId: string, siteId?: string, expiresHours?: number) =>
    api.post('/enrollment-tokens', { clientId, siteId, expiresHours }),
  enroll: (token: string, hostname: string) =>
    api.post('/devices/enroll', { enrollmentToken: token, hostname }),
};
export const realAgentUpdates = { list: () => api.get('/agent-updates') };
export const realSoftware = { list: () => api.get('/software') };
